import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useFixedDropdownMenu from '../../../Hooks/useFixedDropdownMenu';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminSupportingText,
  AdminFormDropdownChevron,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminHeaderAction,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminFormTwoColGridClass,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  ALLOCATION_SETTINGS,
  BUSINESS_LOCATION_SETTINGS,
  CONDITION_FIELD_LABEL,
  CONDITION_OPERATOR_OPTIONS,
  FINAL_CHECK_OUTCOMES,
  INTENT_AND_ELIGIBILITY_SETTINGS,
  MANDATORY_ELIGIBILITY_CHECKS,
  MESSAGES,
  QUOTE_MATCHING_DISTRIBUTION_ROUTE,
  QUOTE_MATCHING_TRADE_CATEGORIES,
  RULE_STATUS_EDIT_OPTIONS,
  SELECTION_ORDER,
  buildSampleQuoteMatchingRules,
  createDefaultQuoteMatchingForm,
  createEmptyCondition,
  deriveQuoteMatchingDisplayStatus,
  formatConditionsDisplay,
  formatQuoteHistoryDisplay,
  formatQuoteMatchingDateTime,
  getQuoteMatchingStatusLabel,
  getReferencedCategoryIds,
  getTradeCategoryById,
  normalizeQuoteMatchingForm,
} from '../../../data/quoteMatchingDistributionConfig';
import {
  getChangedFields,
  mergeRulesAfterSave,
  validateQuoteMatchingRule,
} from '../../../data/quoteMatchingDistributionRuleWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const LockedBlock = ({ title, rows }) => (
  <div className="space-y-3 w-full min-w-0">
    <AdminDetailFieldLabel className="!mb-0">{title}</AdminDetailFieldLabel>
    <div className="space-y-3">
      {rows.map((row) => (
        <AdminConfigLockedField
          key={row.key || row.value || row.label || row.situation}
          label={row.label || row.situation}
          value={row.value || row.requiredResult || row.result}
          isView={false}
        />
      ))}
    </div>
  </div>
);

const useDropdownClickOutside = (open, anchorRef, menuRef, onClose) => {
  useEffect(() => {
    if (!open) return undefined;
    const listener = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [open, anchorRef, menuRef, onClose]);
};

const SearchableSelect = ({
  value,
  options,
  onChange,
  error,
  placeholder,
  getOptionLabel = (o) => o.name,
  getOptionValue = (o) => o.id,
}) => {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { menuRef, layout, placementReady } = useFixedDropdownMenu(open, containerRef);
  const close = () => setOpen(false);
  useDropdownClickOutside(open, containerRef, menuRef, close);

  const selected = (options || []).find((o) => getOptionValue(o) === value);
  const filtered = (options || []).filter((opt) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const label = getOptionLabel(opt).toLowerCase();
    return label.includes(q) || String(getOptionValue(opt)).toLowerCase().includes(q);
  });

  return (
    <div className="relative w-full min-w-0" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`${adminFormDetailSelectClasses(false, Boolean(error))} flex items-center justify-between gap-2`}
      >
        <span className={`min-w-0 flex-1 truncate text-left ${selected ? 'text-offblack' : 'text-mediumgray'}`}>
          {selected ? getOptionLabel(selected) : placeholder}
        </span>
        <AdminFormDropdownChevron open={open} />
      </button>
      {open && layout && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[200] bg-white border border-lightgray rounded-xl shadow-lg max-h-56 overflow-y-auto py-1"
          style={{
            top: layout.top,
            left: layout.left,
            width: layout.width,
            opacity: placementReady ? 1 : 0,
          }}
        >
          <div className="px-2 pb-1 sticky top-0 bg-white">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Trade Category"
              className="w-full text-sm border border-lightgray rounded-lg px-2 py-1.5 outline-none focus:border-primaryblue"
            />
          </div>
          {filtered.map((opt) => (
            <button
              key={getOptionValue(opt)}
              type="button"
              className={`block w-full text-left px-3 py-1.5 text-sm hover:text-primaryblue ${
                getOptionValue(opt) === value ? 'text-primaryblue' : 'text-offblack'
              }`}
              onClick={() => {
                onChange(getOptionValue(opt));
                close();
                setSearch('');
              }}
            >
              {getOptionLabel(opt)}
            </button>
          ))}
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-mediumgray">No Trade Categories found</p>
          ) : null}
        </div>,
        document.body,
      )}
    </div>
  );
};

const QuoteMatchingDistributionRuleDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;
  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleQuoteMatchingRules(),
    [existingRulesFromState],
  );

  const resolvedSource = useMemo(() => {
    if (isCreate) return createDefaultQuoteMatchingForm();
    const found =
      (ruleFromState?.id && allRules.find((r) => r.id === ruleFromState.id))
      || allRules.find((r) => matchesUrlName(r.ruleName, ruleName))
      || ruleFromState;
    return found ? normalizeQuoteMatchingForm(found) : createDefaultQuoteMatchingForm();
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [baseline] = useState(isEdit ? resolvedSource : null);
  const [openedUpdatedOn] = useState(resolvedSource.updatedOn || '');
  const [errors, setErrors] = useState({});
  const [rulesSnapshot, setRulesSnapshot] = useState(allRules);

  useEffect(() => {
    setFormData(resolvedSource);
    setRulesSnapshot(allRules);
  }, [resolvedSource, allRules]);

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key] && !prev.form) return prev;
      const next = { ...prev };
      delete next[key];
      delete next.form;
      return next;
    });
  };

  const updateCondition = (index, patch) => {
    setFormData((prev) => {
      const next = [...(prev.conditions || [])];
      next[index] = { ...next[index], ...patch, field: next[index]?.field || 'trade_category' };
      return { ...prev, conditions: next };
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`conditions.${index}`];
      delete next.conditions;
      delete next.form;
      return next;
    });
  };

  const addCondition = () => {
    setFormData((prev) => ({
      ...prev,
      conditions: [...(prev.conditions || []), createEmptyCondition()],
    }));
  };

  const removeCondition = (index) => {
    setFormData((prev) => ({
      ...prev,
      conditions: (prev.conditions || []).filter((_, i) => i !== index),
    }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith('conditions.')) delete next[key];
      });
      delete next.conditions;
      delete next.form;
      return next;
    });
  };

  const referencedCategories = useMemo(() => {
    const ids = getReferencedCategoryIds(formData.conditions || []);
    return ids.map((id) => {
      const tc = getTradeCategoryById(id);
      return {
        id,
        name: tc?.name || id,
        sourceUpdatedOn: tc?.sourceUpdatedOn || '',
        missing: !tc,
      };
    });
  }, [formData.conditions]);

  const changedFields = useMemo(() => {
    if (!isEdit || !baseline) return [];
    return getChangedFields(baseline, formData);
  }, [isEdit, baseline, formData]);

  const effectiveStatusLabel = formData.id
    ? getQuoteMatchingStatusLabel(
      formData.status || deriveQuoteMatchingDisplayStatus(formData, rulesSnapshot),
    )
    : '—';

  const validationMessages = useMemo(() => {
    const list = [];
    if (errors.form) list.push(errors.form);
    if (errors.ruleName) list.push(errors.ruleName);
    if (errors.ruleStatus) list.push(errors.ruleStatus);
    if (errors.quoteHistoryDays) list.push(errors.quoteHistoryDays);
    if (errors.changeReason) list.push(errors.changeReason);
    if (errors.conditions) list.push(errors.conditions);
    Object.keys(errors)
      .filter((key) => key.startsWith('conditions.'))
      .forEach((key) => list.push(errors[key]));
    return [...new Set(list.filter(Boolean))];
  }, [errors]);

  const handleSave = (e) => {
    e?.preventDefault?.();
    const validation = validateQuoteMatchingRule(formData, rulesSnapshot, formData.id || undefined, {
      isEdit,
    });
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    const result = mergeRulesAfterSave(rulesSnapshot, formData, {
      isEdit,
      existingId: formData.id,
      openedUpdatedOn,
    });

    if (!result.ok) {
      setErrors({ form: result.message || MESSAGES.staleEdit });
      return;
    }

    navigate(QUOTE_MATCHING_DISTRIBUTION_ROUTE, {
      state: { rules: result.rules, feedback: result.message },
    });
  };

  const pageHeading = isCreate
    ? 'Add Quote Matching Rule'
    : isEdit
      ? 'Edit Quote Matching Rule'
      : 'Quote Matching Rule Detail';

  const renderFixedRequirements = (asView = false) => (
    <div className="space-y-4 w-full min-w-0">
      {asView ? (
        <>
          {INTENT_AND_ELIGIBILITY_SETTINGS.map((row) => (
            <TextValue key={row.key} label={row.label} value={row.value} />
          ))}
          {MANDATORY_ELIGIBILITY_CHECKS.map((row) => (
            <TextValue key={row.value} label={row.label} value={row.requiredResult} />
          ))}
          {BUSINESS_LOCATION_SETTINGS.map((row) => (
            <TextValue key={row.key} label={row.label} value={row.value} />
          ))}
          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>Selection Order</AdminDetailFieldLabel>
            <ol className="list-decimal pl-5 space-y-1">
              {SELECTION_ORDER.map((item) => (
                <li key={item} className="text-sm text-offblack">{item}</li>
              ))}
            </ol>
          </div>
          {ALLOCATION_SETTINGS.map((row) => (
            <TextValue key={row.key} label={row.label} value={row.value} />
          ))}
          {FINAL_CHECK_OUTCOMES.map((row) => (
            <TextValue key={row.situation} label={row.situation} value={row.result} />
          ))}
        </>
      ) : (
        <>
          <LockedBlock title="Quote Intent and Service Eligibility" rows={INTENT_AND_ELIGIBILITY_SETTINGS} />
          <LockedBlock title="Business Eligibility and Capacity" rows={MANDATORY_ELIGIBILITY_CHECKS} />
          <LockedBlock title="Business Location" rows={BUSINESS_LOCATION_SETTINGS} />
          <div className="space-y-2 w-full min-w-0">
            <AdminDetailFieldLabel>Business Selection order</AdminDetailFieldLabel>
            <ol className="list-decimal pl-5 space-y-1">
              {SELECTION_ORDER.map((item) => (
                <li key={item} className="text-sm text-offblack">{item}</li>
              ))}
            </ol>
          </div>
          <LockedBlock title="Allocation and Distribution" rows={ALLOCATION_SETTINGS} />
          <LockedBlock title="Final Checks and Outcomes" rows={FINAL_CHECK_OUTCOMES} />
        </>
      )}
    </div>
  );

  const renderReferencedTradeCategories = () => {
    if (!referencedCategories.length) return null;
    return (
      <div className="space-y-3">
        <AdminDetailFieldLabel className="!mb-0">Referenced Trade Categories</AdminDetailFieldLabel>
        <div className="overflow-x-auto min-w-0">
          <div
            className="grid w-full min-w-[480px] py-2 border-b border-lightgray gap-x-3"
            style={{ gridTemplateColumns: 'minmax(90px, 0.6fr) minmax(120px, 1fr) minmax(140px, 1fr)' }}
          >
            <AdminTableHeader>Trade Category ID</AdminTableHeader>
            <AdminTableHeader>Trade Category</AdminTableHeader>
            <AdminTableHeader>Source Last Updated</AdminTableHeader>
          </div>
          {referencedCategories.map((row) => (
            <div
              key={row.id}
              className="grid w-full min-w-[480px] py-2 border-b border-lightgray last:border-b-0 gap-x-3"
              style={{ gridTemplateColumns: 'minmax(90px, 0.6fr) minmax(120px, 1fr) minmax(140px, 1fr)' }}
            >
              <AdminTableMeta className={row.missing ? 'text-alertred' : ''}>{row.id}</AdminTableMeta>
              <AdminTableBody>
                {row.missing ? `${row.name} Unavailable` : row.name}
              </AdminTableBody>
              <AdminTableBody>
                {row.sourceUpdatedOn ? formatQuoteMatchingDateTime(row.sourceUpdatedOn) : '—'}
              </AdminTableBody>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderConditionsEditor = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <AdminDetailFieldLabel className="!mb-0">Optional Conditions</AdminDetailFieldLabel>
        <button
          type="button"
          onClick={addCondition}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
        >
          <i className="fi fi-rs-plus text-xs" aria-hidden />
          Add Condition
        </button>
      </div>
      {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
      {!(formData.conditions || []).length ? (
        <AdminDetailFieldValue className="py-2">No Conditions</AdminDetailFieldValue>
      ) : (
        <div className="space-y-3">
          {(formData.conditions || []).map((condition, index) => (
            <div key={condition.id || index} className="rounded-lg border border-lightgray p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <AdminSupportingText className="mb-0 font-semibold !text-offblack">Condition</AdminSupportingText>
                <button
                  type="button"
                  onClick={() => removeCondition(index)}
                  className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
                  aria-label="Remove Condition"
                >
                  <i className="fi fi-rs-trash text-sm" aria-hidden />
                </button>
              </div>
              {errors[`conditions.${index}`] ? (
                <AdminFormFieldError>{errors[`conditions.${index}`]}</AdminFormFieldError>
              ) : null}
              <AdminConfigLockedField label="Field" value={CONDITION_FIELD_LABEL} isView={false} />
              <div className="space-y-1 w-full min-w-0">
                <AdminDetailFieldLabel>
                  Operator <span className="text-alertred">*</span>
                </AdminDetailFieldLabel>
                <div className="relative w-full min-w-0">
                  <select
                    value={condition.operator || ''}
                    onChange={(e) => updateCondition(index, { operator: e.target.value })}
                    className={adminFormDetailSelectClasses(false, Boolean(errors[`conditions.${index}`]))}
                  >
                    <option value="">Select operator</option>
                    {CONDITION_OPERATOR_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <AdminFormDropdownChevron />
                </div>
              </div>
              <div className="space-y-1 w-full min-w-0">
                <AdminDetailFieldLabel>
                  Value <span className="text-alertred">*</span>
                </AdminDetailFieldLabel>
                <SearchableSelect
                  value={condition.tradeCategoryId}
                  options={
                    condition.tradeCategoryId
                    && !QUOTE_MATCHING_TRADE_CATEGORIES.some((tc) => tc.id === condition.tradeCategoryId)
                      ? [
                        ...QUOTE_MATCHING_TRADE_CATEGORIES,
                        { id: condition.tradeCategoryId, name: `${condition.tradeCategoryId} Unavailable` },
                      ]
                      : QUOTE_MATCHING_TRADE_CATEGORIES
                  }
                  onChange={(id) => updateCondition(index, { tradeCategoryId: id })}
                  error={errors[`conditions.${index}`]}
                  placeholder="Select Trade Category"
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {renderReferencedTradeCategories()}
    </div>
  );

  if (isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>{pageHeading}</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <TextValue label="Rule ID" value={formData.id} mono />
              <TextValue label="Rule Name" value={formData.ruleName} />
              <TextValue
                label="Rule Status"
                value={formData.ruleStatus === 'inactive' ? 'Inactive' : 'Active'}
              />
              <TextValue label="Effective Status" value={effectiveStatusLabel} />
              <TextValue label="Quote History" value={formatQuoteHistoryDisplay(formData.quoteHistoryDays)} />
              <div className="sm:col-span-2">
                <TextValue
                  label="Conditions"
                  value={formatConditionsDisplay(formData.conditions)}
                />
              </div>
              <TextValue label="Date Added" value={formatQuoteMatchingDateTime(formData.createdOn)} />
              <TextValue label="Added By" value={formData.createdBy} />
              <TextValue label="Last Updated" value={formatQuoteMatchingDateTime(formData.updatedOn)} />
              <TextValue label="Last Updated By" value={formData.updatedBy} />
            </div>
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <AdminPanelTitle className="text-offblack mb-4">Fixed Requirements</AdminPanelTitle>
            {renderFixedRequirements(true)}
          </DashboardMainContainer>

          {referencedCategories.length ? (
            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              <AdminPanelTitle className="text-offblack mb-4">Referenced Trade Categories</AdminPanelTitle>
              {renderReferencedTradeCategories()}
            </DashboardMainContainer>
          ) : null}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage title={pageHeading} onSubmit={handleSave} showSave={false}>
        {/* 1. Rule Details */}
        <div className="space-y-4 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Rule Details</AdminPanelTitle>
          {formData.id ? (
            <AdminConfigLockedField
              label="Rule ID"
              value={formData.id}
              mono
              isView={false}
            />
          ) : null}
          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Rule Name <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <input
              type="text"
              value={formData.ruleName}
              maxLength={100}
              onChange={(e) => updateField('ruleName', e.target.value)}
              placeholder="Enter Rule Name"
              className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
            />
            {errors.ruleName ? <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError> : null}
          </div>
          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Rule Status <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <div className="relative w-full min-w-0">
              <select
                value={formData.ruleStatus || 'active'}
                onChange={(e) => updateField('ruleStatus', e.target.value)}
                className={adminFormDetailSelectClasses(false, Boolean(errors.ruleStatus))}
              >
                {RULE_STATUS_EDIT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
            {errors.ruleStatus ? <AdminFormFieldError>{errors.ruleStatus}</AdminFormFieldError> : null}
          </div>
          <AdminConfigLockedField
            label="Effective Status"
            value={effectiveStatusLabel}
            isView={false}
          />
          {!isCreate ? (
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <AdminConfigLockedField
                label="Date Added"
                value={formatQuoteMatchingDateTime(formData.createdOn)}
                isView={false}
              />
              <AdminConfigLockedField label="Added By" value={formData.createdBy || '—'} isView={false} />
              <AdminConfigLockedField
                label="Last Updated"
                value={formatQuoteMatchingDateTime(formData.updatedOn)}
                isView={false}
              />
              <AdminConfigLockedField
                label="Last Updated By"
                value={formData.updatedBy || '—'}
                isView={false}
              />
            </div>
          ) : null}
        </div>

        {/* 2. Rule Settings */}
        <div className="space-y-4 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Rule Settings</AdminPanelTitle>
          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Quote History <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <div className="flex items-center gap-2 w-full min-w-0">
              <input
                type="number"
                min={1}
                step={1}
                value={formData.quoteHistoryDays}
                onChange={(e) => updateField('quoteHistoryDays', e.target.value)}
                placeholder="Days"
                className={`${adminFormDetailInputClasses(false, Boolean(errors.quoteHistoryDays))} flex-1 min-w-0`}
              />
              <span className="text-sm text-offblack shrink-0">Days</span>
            </div>
            {errors.quoteHistoryDays ? (
              <AdminFormFieldError>{errors.quoteHistoryDays}</AdminFormFieldError>
            ) : null}
          </div>
          {renderFixedRequirements(false)}
        </div>

        {/* 3. Optional Conditions */}
        <div className="space-y-4 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Optional Conditions</AdminPanelTitle>
          {renderConditionsEditor()}
        </div>

        {/* 4. Validation Messages */}
        <div className="space-y-3 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Validation Messages</AdminPanelTitle>
          {validationMessages.length ? (
            <div className="space-y-2">
              {validationMessages.map((message) => (
                <AdminFormFieldError key={message}>{message}</AdminFormFieldError>
              ))}
            </div>
          ) : (
            <AdminDetailFieldValue className="py-2">—</AdminDetailFieldValue>
          )}
        </div>

        {/* 5. Review and Save */}
        <div className="space-y-4 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Review and Save</AdminPanelTitle>
          {isEdit ? (
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Change Reason <span className="text-alertred">*</span>
              </AdminDetailFieldLabel>
              <textarea
                value={formData.changeReason}
                onChange={(e) => updateField('changeReason', e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Enter a Change Reason"
                className={adminFormDetailTextareaClasses(false, Boolean(errors.changeReason))}
              />
              {errors.changeReason ? (
                <AdminFormFieldError>{errors.changeReason}</AdminFormFieldError>
              ) : null}
            </div>
          ) : null}
          {isEdit && changedFields.length ? (
            <div className="space-y-2">
              <AdminDetailFieldLabel className="!mb-0">Proposed changes</AdminDetailFieldLabel>
              {changedFields.includes('conditions') ? (
                <AdminDetailFieldValue className="py-1">
                  Conditions: {formatConditionsDisplay(baseline?.conditions)} →{' '}
                  {formatConditionsDisplay(formData.conditions)}
                </AdminDetailFieldValue>
              ) : null}
              {changedFields.includes('quoteHistoryDays') ? (
                <AdminDetailFieldValue className="py-1">
                  Quote History: {baseline?.quoteHistoryDays || '—'} → {formData.quoteHistoryDays || '—'} days
                </AdminDetailFieldValue>
              ) : null}
              {changedFields.includes('ruleName') ? (
                <AdminDetailFieldValue className="py-1">
                  Rule Name: {baseline?.ruleName} → {formData.ruleName}
                </AdminDetailFieldValue>
              ) : null}
              {changedFields.includes('ruleStatus') ? (
                <AdminDetailFieldValue className="py-1">
                  Rule Status: {baseline?.ruleStatus === 'inactive' ? 'Inactive' : 'Active'} →{' '}
                  {formData.ruleStatus === 'inactive' ? 'Inactive' : 'Active'}
                </AdminDetailFieldValue>
              ) : null}
            </div>
          ) : null}
          <div className="flex items-center justify-end">
            <AdminHeaderAction type="submit">Save</AdminHeaderAction>
          </div>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default QuoteMatchingDistributionRuleDetail;
